import { ViewConfig } from "@lib/views";
import {
    DATE_COLUMN_SIZE,
    AMOUNT_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    ResourceType,
} from "@common/types/auditLog";
import { HistoryDetailsDateCell } from "../../cells/HistoryDetailsDateCell";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryDetailsDescriptionCell } from "../../cells/HistoryDetailsDescriptionCell";
import {
    HistoryTransactionsDetailsOldValueCell,
    HistoryTransactionsDetailsNewValueCell,
} from "../cells/HistoryTransactionsDetailsValueCell";
import { HistoryDetailsAmountCell } from "../../cells/HistoryDetailsAmountCell";
import { HistoryDetailsEntityCell } from "../../cells/HistoryDetailsEntityCell";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryTransactionsDetailsArrowCell } from "../cells/HistoryTransactionsDetailsArrowCell";
import { HistoryUpdatedTransactionFinancialAccountCell } from "../cells/HistoryUpdatedTransactionFinancialAccountCell";
import { HistoryTransactionDetailsColumn } from "./historyTransactionDetailsView";

export interface HistoryTransactionDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    logDate: Date;
    changeType: AuditLogChangeType;
    resourceType: ResourceType;
    resourceId: string;
    resource?: Record<string, unknown>;
    field: string;
    fieldLabel: string;
    oldValue: AuditLogDiff["before"];
    newValue: AuditLogDiff["after"];
    financialAccountId?: number;
    amount: number;
    /**
     * Optional secondary label rendered alongside the resource description,
     * used to describe the specific nested change (e.g. which line item
     * within a bill/invoice was affected).
     */
    subDescription?: string;
}

const DISPLAYABLE_FIELDS: Record<string, string> = {
    name: "Name",
    order: "Order",
    type: "Type",
    icon: "Icon",
    isDefault: "Default",
    categoryId: "Category",
    entityId: "Entity",
    counterpartyId: "Counterparty",
    pairedTransferId: "Transfer match",
    financialDocumentId: "Receipt",
    splitParentId: "Split",
    accountId: "Cash ledger",
    accrualAccountId: "Accrual ledger",
    accountsReceivableId: "Receivables account",
    accountsPayableId: "Payables account",
    generalLedgerAccountId: "Cash ledger",
    financialAccountId: "Financial account",
    memo: "Memo",
    description: "Description",
    bankDescription: "Bank description",
    amount: "Amount",
    date: "Date",
    dateOnly: "Date",
    status: "Status",
    assigneeId: "Assignee",
    dueDate: "Due date",
    priority: "Priority",
    title: "Title",
    reconciledInId: "Reconciliation",
    classIds: "Class",
    lines: "Line item",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    currency: "Currency",
    invoiceNumber: "Number",
    due: "Due date",
    paidAt: "Paid date",
    uncollectableAt: "Uncollectible date",
    voidedAt: "Void date",
    source: "Source",
    isSubscription: "Subscription",
    label: "Label",
    parentClassId: "Parent class",
    rootClassId: "Root class",
    fileName: "File name",
    documentCategoryId: "Category",
    loanId: "Loan",
    excludeFrom1099: "Excluded from 1099",
    excludeFromCashFlowReport: "Excluded from cash flow report",
};

export const getFieldLabel = (field: string): string | undefined =>
    DISPLAYABLE_FIELDS[field];

export const filterDisplayableDiffs = (
    diffs: HistoryDetailsItem["diff"],
): AuditLogDiff[] =>
    diffs.filter((diff) => {
        const fieldLabel = getFieldLabel(diff.field);
        if (!fieldLabel) {
            return false;
        }
        const hasLabel = diff.before.valueLabel ?? diff.after.valueLabel;
        const hasValue =
            diff.before.value !== null || diff.after.value !== null;
        return hasLabel ?? hasValue;
    });

const DEFAULT_OLD_VALUE_LABEL = "Previous value";
const DEFAULT_NEW_VALUE_LABEL = "Modified value";

const getSingleFieldLabel = (
    data: HistoryTransactionDetailsRow[],
): string | null => {
    const fieldValues = data
        .map((row) => row.field)
        .filter((field) => field.length > 0);
    const uniqueFields = Array.from(new Set(fieldValues));
    if (uniqueFields.length !== 1) {
        return null;
    }
    const [field] = uniqueFields;
    return getFieldLabel(field) ?? field;
};

export const createHistoryTransactionDetailsViewConfig = (
    data: HistoryTransactionDetailsRow[],
): ViewConfig<
    HistoryTransactionDetailsRow,
    HistoryTransactionDetailsColumn,
    never
> => {
    const fieldLabel = getSingleFieldLabel(data);
    const oldValueLabel = fieldLabel
        ? `Prior ${fieldLabel.toLowerCase()}`
        : DEFAULT_OLD_VALUE_LABEL;
    const newValueLabel = fieldLabel
        ? `Updated ${fieldLabel.toLowerCase()}`
        : DEFAULT_NEW_VALUE_LABEL;

    return {
        columns: {
            [HistoryTransactionDetailsColumn.LOG_TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                minSize: 120,
                maxSize: 150,
                immutable: true,
                hiddenFromSettings: true,
            },
            [HistoryTransactionDetailsColumn.DATE]: {
                name: "Date",
                header: "Date",
                accessorKey: "date",
                cell: HistoryDetailsDateCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryTransactionDetailsColumn.DESCRIPTION]: {
                name: "Description",
                header: "Description",
                accessorKey: "changeType",
                cell: HistoryDetailsDescriptionCell,
                minSize: 400,
                maxSize: 400,
            },
            [HistoryTransactionDetailsColumn.OLD_VALUE]: {
                name: oldValueLabel,
                header: oldValueLabel,
                accessorKey: "oldValue",
                cell: HistoryTransactionsDetailsOldValueCell,
                minSize: 200,
                maxSize: 250,
            },
            [HistoryTransactionDetailsColumn.NEW_VALUE]: {
                name: newValueLabel,
                header: newValueLabel,
                accessorKey: "newValue",
                cell: HistoryTransactionsDetailsNewValueCell,
                minSize: 200,
                maxSize: 250,
            },
            [HistoryTransactionDetailsColumn.FINANCIAL_ACCOUNT]: {
                name: "Financial account",
                header: "Financial account",
                accessorKey: "financialAccountId",
                cell: HistoryUpdatedTransactionFinancialAccountCell,
                minSize: 200,
                maxSize: 400,
            },
            [HistoryTransactionDetailsColumn.AMOUNT]: {
                name: "Amount",
                header: "Amount",
                accessorKey: "amount",
                cell: HistoryDetailsAmountCell,
                ...AMOUNT_COLUMN_SIZE,
            },
            [HistoryTransactionDetailsColumn.ENTITY]: {
                name: "Entity",
                header: "Entity",
                accessorKey: "entityId",
                cell: HistoryDetailsEntityCell,
                minSize: 56,
                maxSize: 150,
                immutable: true,
                hiddenFromSettings: true,
            },
            [HistoryTransactionDetailsColumn.ACTIONS]: {
                name: "Actions",
                header: "",
                accessorKey: "id",
                cell: HistoryTransactionsDetailsArrowCell,
                minSize: 56,
                maxSize: 56,
                immutable: true,
                hiddenFromSettings: true,
            },
        },
    };
};
